import { FinishedIcon } from "@/ui-components/Icons";
import { Loader } from "@/ui-components/Loader";
import { MENU_WIDTH } from "@/ui-components/layouts/constants";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Button, List, ListItem, ListItemButton, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItemText,
  ListSubheader,
  StatusBox,
} from "../components/Arrordion";
import { useGetData } from "./hooks";
import { Menu, TaskComparisonViewParams } from "./types";
import { groupIsActive } from "./utils";

const TasksComparisonMenu: React.FC = () => {
  const { protocolQuestionId, assessmentId } = useParams<TaskComparisonViewParams>() as TaskComparisonViewParams;
  const { t } = useTranslation();
  // List of open tabs (themes). Tab is represented by ids of its *first* question.
  const [expanded, setExpanded] = React.useState<number[]>([]);

  const { questions, themes, menuList, isLoading } = useGetData();

  useEffect(() => {
    // The effect is used to set the default value of _expanded_.
    // Since a question may *not* be the first for its tab (theme),
    // we need to find its group and then find the id of its first question.
    if (!protocolQuestionId) {
      return;
    }
    const group = menuList?.find(g =>
      g.sub_groups.find(sub_group => sub_group.questions.some(question => question.id === Number(protocolQuestionId))),
    );
    const question = group?.sub_groups?.[0].questions?.[0];
    if (question) {
      setExpanded([question.id]);
    }
  }, [JSON.stringify(questions), protocolQuestionId]);

  const toggleMenuItem = (group: Menu) => () => {
    const panel = group.sub_groups[0].questions?.[0]?.id;
    if (!panel) {
      return;
    }
    if (expanded.includes(panel)) {
      setExpanded(expanded.filter(p => p !== panel));
    } else {
      setExpanded([...expanded, panel]);
    }
  };

  if (isLoading) {
    return <Loader sx={{ width: "250px" }} />;
  }
  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        width: MENU_WIDTH,
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "right",
          justifyContent: "right",
          borderBottom: "1px solid var(--green-grey-scale-100)",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => setExpanded([])}
          sx={{ my: 2, mr: 2 }}
          endIcon={<ExpandLess />}
          disabled={themes && themes.length === 0}
        >
          {t("general.Collapse_all")}
        </Button>
      </Box>
      {menuList!.map(group => (
        <Accordion key={group.group_id} expanded={groupIsActive(group, expanded)} onChange={toggleMenuItem(group)}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls={`${group.group_id}-content`}
            id={`${group.group_id}-header`}
          >
            <Typography
              sx={{
                fontWeight: groupIsActive(group, expanded) ? "700 !important" : "400",
              }}
            >
              {group.group_name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List
              sx={{
                px: 0,
                py: 0,
              }}
            >
              {group.sub_groups.map(sub_group => (
                <ListItem
                  key={sub_group.sub_group_id}
                  sx={{
                    px: 0,
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <ListSubheader component="div">{sub_group.sub_group_name}</ListSubheader>
                    <List component="div">
                      {sub_group.questions.map(({ id, name }) => (
                        <ListItem
                          component="div"
                          sx={{
                            px: 0,
                            py: 0,
                          }}
                          key={id}
                        >
                          <ListItemButton
                            component={Link}
                            to={`/assessments/${assessmentId}/protocol_questions/${id}/tasks`}
                            sx={[
                              {
                                px: 2,
                                paddingRight: "32px",
                              },
                              String(id) === protocolQuestionId
                                ? {
                                    background: "#E0F2EF",
                                  }
                                : {
                                    background: "unset",
                                  },
                            ]}
                          >
                            <StatusBox>
                              <FinishedIcon />
                            </StatusBox>
                            <ListItemText>{name}</ListItemText>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default TasksComparisonMenu;
